import { Component, DestroyRef, HostListener, inject, Input, OnChanges, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Point } from 'highcharts';
import { fromEvent, tap } from 'rxjs';

@Component({
  selector: 'app-legend-item',
  imports: [],
  templateUrl: './legend-item.component.html',
  styleUrl: './legend-item.component.scss',
})
export class LegendItemComponent implements OnInit, OnChanges {
  @Input({ required: true }) id!: string;
  @Input() chart?: Highcharts.Chart;
  @Input() title = '';
  @Input() level = 0;
  @Input() color?: string;
  @Input() forceDisplay = false;
  @Input() subtitle: (c?: number, t?: number) => string = () => '';
  @Input() compact = false;
  @Input() children: string[] = [];
  @Input() update = false; // trigger re-render
  private destroyRef = inject(DestroyRef);
  _subtitle = '';
  show = false;
  legendColor?: string;
  slice?: Point;
  childrenSlices?: Point[];

  @HostListener('click') onClick() {
    document.dispatchEvent(new CustomEvent('legend-item-clicked'));
  }

  ngOnInit() {
    this.updateOnClick();
  }

  /**
   * When a legend item is clicked, it changes the total and percentage values
   * so other legend-items need to be updated accordingly
   */
  updateOnClick() {
    fromEvent(document, 'legend-item-clicked')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.doUpdate())
      )
      .subscribe();
  }

  ngOnChanges() {
    this.doUpdate();
  }

  isActive() {
    return !!this.slice || this.children.length;
  }

  doUpdate() {
    this.setSlice();
    this.setChildrenSlices();
    this.show = !!this.slice || this.forceDisplay;
    this.legendColor = (this.slice?.color as string) ?? this.color;
    this._subtitle = this.getSubtitle();
  }

  getSubtitle() {
    const slices = this.slice ? [this.slice] : this.childrenSlices;
    const visibleCount = this.getVisibleCount(slices);
    return this.subtitle(visibleCount, this.getVisibleTotal());
  }

  getVisibleCount(slices?: Point[]) {
    return slices
      ?.filter((point: Point) => point.visible)
      .reduce((sum, point: Point) => {
        return sum + (point.y ?? 0);
      }, 0);
  }

  getVisibleTotal() {
    return this.chart?.series?.[0]?.points
      ?.filter((point: Point) => point.visible)
      .reduce((sum, point: Point) => {
        return sum + (point.y ?? 0);
      }, 0);
  }

  isHidden(): boolean {
    if (this.childrenSlices) {
      return this.childrenSlices.every((slice) => slice?.visible === false);
    }

    return !!this.slice && !this.slice.visible;
  }

  toggle() {
    if (this.slice) {
      this.slice.setVisible(!this.slice.visible);
    }

    if (this.childrenSlices) {
      const areVisible = this.childrenSlices.some((slice) => slice?.visible === true);
      this.childrenSlices.forEach((slice) => slice?.setVisible(!areVisible));
    }
  }

  setSlice() {
    this.slice = this.getSlice(this.id);
  }

  setChildrenSlices() {
    this.childrenSlices =
      this.children.length > 0 ? this.children.map((id) => this.getSlice(id)).filter((slice) => !!slice) : undefined;
  }

  private getSlice(id: string): Point | undefined {
    return this.chart?.series?.[0]?.data?.find((point) => point.options.id === id);
  }
}
